import { inViewMargin } from '@helpers/animate';
import { Device, from } from '@helpers/media';
import { withMotionMax } from '@hoc/withMotionMax';
import { useInView } from '@hooks/useInView';
import useMedia from '@hooks/useMedia';
import SvgNext from '@img/icons/carousel-next.svg';
import SvgPrev from '@img/icons/carousel-prev.svg';
import ApproachCard from '@stories/Components/ApproachCard/ApproachCard';
import Button from '@stories/Components/Button/Button';
import DotPagination from '@stories/Components/DotPagination/DotPagination';
import { useAnimation, useReducedMotion } from 'framer-motion';
import * as React from 'react';
import { useSwipeable } from 'react-swipeable';
import S from './ApproachSection.styles';
const ApproachSection = ({ bgTheme = 'lightGrey', bgColor = 'white', content, desktopVisibleCount = 4, id, items, title, }) => {
    const [refContainer, inView] = useInView({ rootMargin: inViewMargin });
    const visibleCount = useMedia([from(Device.DesktopLarge), from(Device.TabletLarge), from(Device.TabletSmall)], [desktopVisibleCount, 3, 2], 1);
    const [activeIndex, setActiveIndex] = React.useState(0);
    // Calculate the max index where all items up to the last are visible.
    const maxIndex = items.length - visibleCount;
    // Shared item transition settings.
    const prefersReducedMotion = useReducedMotion();
    const itemDuration = prefersReducedMotion ? 0 : 0.6;
    const itemTransition = {
        duration: itemDuration,
        ease: 'easeOut',
    };
    // Handle item transition when index changes.
    const sliderControls = useAnimation();
    React.useEffect(() => {
        // Animate slider to correct position.
        sliderControls.start({
            x: `-${activeIndex * (100 / visibleCount)}%`,
        }, itemTransition);
    }, [activeIndex]);
    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            setActiveIndex(Math.min(activeIndex + 1, items.length - 1));
        },
        onSwipedRight: () => {
            setActiveIndex(Math.max(activeIndex - 1, 0));
        },
    });
    React.useEffect(() => {
        // Go back to the first item when the layout changes.
        setActiveIndex(0);
    }, [visibleCount]);
    return (React.createElement(S.Container, { ref: refContainer, id: id, "data-bg": bgColor, "data-inview": inView, "data-overflow": visibleCount < items.length, "data-theme": bgTheme, style: { '--itemWidth': `${100 / visibleCount}%` } },
        React.createElement(S.Inner, null,
            React.createElement(S.Title, { id: title.replaceAll(/[\W_]+/g, '-').toLowerCase() }, title),
            content && React.createElement(S.Content, { dangerouslySetInnerHTML: { __html: content } }),
            React.createElement(S.ItemListWrapper, null,
                React.createElement(S.ItemList, { animate: sliderControls, ...swipeHandlers }, items.map((item, index) => (React.createElement(S.Item, { key: item.id, onFocus: () => {
                        // Scroll focused item into view.
                        setActiveIndex(Math.min(index, maxIndex));
                    } },
                    React.createElement(ApproachCard, { ...item })))))),
            visibleCount < items.length && (React.createElement(React.Fragment, null,
                React.createElement(S.PaginationWrapper, null,
                    React.createElement(DotPagination, { count: maxIndex + 1, onChange: (index) => setActiveIndex(index), value: activeIndex, customLayoutId: `ApproachSection_${id}` })),
                React.createElement(S.ControlsWrapper, null,
                    React.createElement(Button, { buttonType: "default", disabled: activeIndex === 0, fillDirection: "left", icon: React.createElement(SvgPrev, null), iconOnly: true, onClick: (event) => {
                            event.preventDefault();
                            setActiveIndex(Math.max(activeIndex - 1, 0));
                        } }, "Previous"),
                    React.createElement(Button, { buttonType: "default", disabled: activeIndex === maxIndex, fillDirection: "right", icon: React.createElement(SvgNext, null), iconOnly: true, onClick: (event) => {
                            event.preventDefault();
                            setActiveIndex(Math.min(activeIndex + 1, maxIndex));
                        } }, "Next")))))));
};
export default withMotionMax(ApproachSection);
